import { createContext, useContext, useState, useId, useRef, KeyboardEvent, ReactNode, HTMLAttributes, ButtonHTMLAttributes } from 'react';

interface TabsContextValue {
  value: string;
  setValue: (v: string) => void;
  idBase: string;
  orientation: 'horizontal' | 'vertical';
  registerTrigger: (v: string, ref: HTMLButtonElement | null) => void;
  triggerRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('Tabs subcomponents muszą być wewnątrz <Tabs>');
  return ctx;
}

export interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  children: ReactNode;
}

export function Tabs({
  defaultValue,
  value: controlled,
  onValueChange,
  orientation = 'horizontal',
  className,
  children,
}: TabsProps) {
  const [internal, setInternal] = useState(defaultValue ?? '');
  const isControlled = controlled !== undefined;
  const value = isControlled ? controlled : internal;
  const idBase = useId();
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const setValue = (v: string) => {
    if (!isControlled) setInternal(v);
    onValueChange?.(v);
  };

  const registerTrigger = (v: string, ref: HTMLButtonElement | null) => {
    if (ref) triggerRefs.current.set(v, ref);
    else triggerRefs.current.delete(v);
  };

  return (
    <TabsContext.Provider value={{ value, setValue, idBase, orientation, registerTrigger, triggerRefs }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps extends HTMLAttributes<HTMLDivElement> {
  /** Jeśli true - tabs zajmują pełną szerokość równomiernie. */
  fullWidth?: boolean;
}

export function TabsList({ fullWidth, className, children, ...rest }: TabsListProps) {
  const { orientation } = useTabsContext();
  return (
    <div
      role="tablist"
      aria-orientation={orientation}
      className={[
        'inline-flex gap-1 bg-surface-raised/60 p-1 rounded-xl border border-border-subtle',
        orientation === 'vertical' ? 'flex-col' : '',
        fullWidth && orientation === 'horizontal' ? 'w-full' : '',
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface TabsTriggerProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string;
  leftIcon?: ReactNode;
  size?: 'sm' | 'md';
}

export function TabsTrigger({ value, leftIcon, size = 'md', className, children, disabled, ...rest }: TabsTriggerProps) {
  const ctx = useTabsContext();
  const isActive = ctx.value === value;

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const keys = ctx.orientation === 'horizontal'
      ? { next: 'ArrowRight', prev: 'ArrowLeft' }
      : { next: 'ArrowDown', prev: 'ArrowUp' };
    if (e.key !== keys.next && e.key !== keys.prev && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const values = Array.from(ctx.triggerRefs.current.keys());
    const currentIdx = values.indexOf(ctx.value);
    let nextIdx = currentIdx;
    if (e.key === keys.next) nextIdx = (currentIdx + 1) % values.length;
    else if (e.key === keys.prev) nextIdx = (currentIdx - 1 + values.length) % values.length;
    else if (e.key === 'Home') nextIdx = 0;
    else if (e.key === 'End') nextIdx = values.length - 1;
    const nextValue = values[nextIdx];
    ctx.setValue(nextValue);
    ctx.triggerRefs.current.get(nextValue)?.focus();
  };

  return (
    <button
      ref={(el) => ctx.registerTrigger(value, el)}
      type="button"
      role="tab"
      id={`${ctx.idBase}-trigger-${value}`}
      aria-selected={isActive}
      aria-controls={`${ctx.idBase}-content-${value}`}
      tabIndex={isActive ? 0 : -1}
      disabled={disabled}
      onClick={() => !disabled && ctx.setValue(value)}
      onKeyDown={handleKeyDown}
      className={[
        'inline-flex items-center justify-center gap-2 font-medium transition-colors duration-fast ease-smooth',
        size === 'sm' ? 'rounded-md px-2 py-1 text-xs' : 'rounded-lg px-4 py-2 text-sm',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        isActive
          ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
          : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover border border-transparent',
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {leftIcon && (
        <span className="inline-flex items-center justify-center w-3.5 h-3.5 shrink-0 [&>svg]:w-full [&>svg]:h-full" aria-hidden="true">
          {leftIcon}
        </span>
      )}
      {children}
    </button>
  );
}

export interface TabsContentProps extends HTMLAttributes<HTMLDivElement> {
  value: string;
}

export function TabsContent({ value, className, children, ...rest }: TabsContentProps) {
  const ctx = useTabsContext();
  if (ctx.value !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${ctx.idBase}-content-${value}`}
      aria-labelledby={`${ctx.idBase}-trigger-${value}`}
      tabIndex={0}
      className={['mt-4 focus:outline-none', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}
